import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

export type PromptSlot = {
  key: string;
  label: string;
  slot_type?: string;
};

type Seg = { t: 'text' | 'ref'; v: string };

function isWordChar(ch: string | undefined): boolean {
  return Boolean(ch && /[\wÀ-ÿ]/.test(ch));
}

function displayName(slot: PromptSlot): string {
  return (slot.label || slot.key).trim();
}

function slotTokens(slots: PromptSlot[]): { raw: string; display: string; slot: PromptSlot }[] {
  const items: { raw: string; display: string; slot: PromptSlot }[] = [];
  slots.forEach((slot) => {
    const name = displayName(slot);
    if (name) items.push({ raw: `/${name}`, display: name, slot });
    if (slot.key) items.push({ raw: `{{${slot.key}}}`, display: name || slot.key, slot });
  });
  return items.sort((a, b) => b.raw.length - a.raw.length);
}

export function tokenizePrompt(value: string, slots: PromptSlot[]): Seg[] {
  const refs = slotTokens(slots);
  const out: Seg[] = [];
  let i = 0;
  while (i < value.length) {
    let hit: (typeof refs)[number] | null = null;
    if (value[i] === '/' || value.startsWith('{{', i)) {
      const slice = value.slice(i);
      hit = refs.find((r) => (
        slice.toLowerCase().startsWith(r.raw.toLowerCase()) && !isWordChar(slice[r.raw.length])
      )) || null;
    }
    if (hit) {
      out.push({ t: 'ref', v: hit.display });
      i += hit.raw.length;
    } else {
      const start = i;
      i += 1;
      while (i < value.length && value[i] !== '/' && !value.startsWith('{{', i)) i += 1;
      out.push({ t: 'text', v: value.slice(start, i) });
    }
  }
  return out;
}

export function slashQueryAt(value: string, caret: number, slots: PromptSlot[] = []): { start: number; query: string } | null {
  const before = value.slice(0, caret);
  const segs = tokenizePrompt(before, slots);
  const last = segs[segs.length - 1];
  if (!last || last.t === 'ref') return null;
  const match = last.v.match(/(^|[\s([{])\/([^\n/]*)$/);
  if (!match) return null;
  return { start: before.length - match[2].length - 1, query: match[2] };
}

function makeChip(name: string): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = 'studio-prompt-chip';
  span.contentEditable = 'false';
  span.dataset.ref = name;
  span.textContent = `/${name}`;
  return span;
}

function serializeEditor(root: HTMLElement): string {
  let out = '';
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += (node.textContent || '').replace(/\u200b/g, '').replace(/\u00a0/g, ' ');
      return;
    }
    if (!(node instanceof HTMLElement)) {
      node.childNodes.forEach(walk);
      return;
    }
    if (node.dataset.ref != null) {
      out += `/${node.dataset.ref}`;
      return;
    }
    if (node.tagName === 'BR') {
      out += '\n';
      return;
    }
    if (node.tagName === 'DIV' || node.tagName === 'P') {
      if (out.length && !out.endsWith('\n')) out += '\n';
      node.childNodes.forEach(walk);
      return;
    }
    node.childNodes.forEach(walk);
  };
  root.childNodes.forEach(walk);
  if (out.endsWith('\n') && root.lastElementChild?.tagName === 'BR' && !root.lastElementChild.nextSibling) {
    /* keep trailing newline */
  }
  return out;
}

function fillEditor(root: HTMLElement, value: string, slots: PromptSlot[]) {
  const segs = tokenizePrompt(value, slots);
  root.replaceChildren();
  if (!value) {
    root.classList.add('is-empty');
    return;
  }
  root.classList.remove('is-empty');
  segs.forEach((seg) => {
    if (seg.t === 'ref') {
      root.appendChild(makeChip(seg.v));
    } else {
      seg.v.split('\n').forEach((part, i) => {
        if (i > 0) root.appendChild(document.createElement('br'));
        if (part) root.appendChild(document.createTextNode(part));
      });
    }
  });
  if (root.lastChild instanceof HTMLElement && root.lastChild.dataset.ref != null) {
    root.appendChild(document.createTextNode('\u200b'));
  }
}

function caretOffset(root: HTMLElement): number {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return 0;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.startContainer) && range.startContainer !== root) return 0;
  const pre = document.createRange();
  pre.selectNodeContents(root);
  try {
    pre.setEnd(range.startContainer, range.startOffset);
  } catch {
    return serializeEditor(root).length;
  }
  const holder = document.createElement('div');
  holder.appendChild(pre.cloneContents());
  return serializeEditor(holder).length;
}

function setCaretOffset(root: HTMLElement, offset: number) {
  const sel = window.getSelection();
  if (!sel) return;
  let left = Math.max(0, offset);
  const place = (node: Node, at: 'before' | 'after' | number) => {
    const r = document.createRange();
    if (at === 'before') r.setStartBefore(node);
    else if (at === 'after') r.setStartAfter(node);
    else r.setStart(node, at);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
  };
  const walk = (node: Node): boolean => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent || '').replace(/\u200b/g, '');
      const raw = node.textContent || '';
      if (left <= text.length) {
        const rawAt = raw.includes('\u200b') && left >= text.length ? raw.length : left;
        place(node, Math.min(rawAt, raw.length));
        return true;
      }
      left -= text.length;
      return false;
    }
    if (node instanceof HTMLElement && node.dataset.ref != null) {
      const len = node.dataset.ref.length + 1;
      if (left <= len) {
        place(node, left === 0 ? 'before' : 'after');
        return true;
      }
      left -= len;
      return false;
    }
    if (node instanceof HTMLElement && node.tagName === 'BR') {
      if (left <= 1) {
        place(node, 'after');
        return true;
      }
      left -= 1;
      return false;
    }
    return Array.from(node.childNodes).some(walk);
  };
  if (!walk(root)) {
    const r = document.createRange();
    r.selectNodeContents(root);
    r.collapse(false);
    sel.removeAllRanges();
    sel.addRange(r);
  }
}

function caretClientRect(): DOMRect | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  const rects = range.getClientRects();
  if (rects.length > 0) return rects[rects.length - 1];
  const br = range.getBoundingClientRect();
  if (br.top || br.left || br.height) return br;
  return null;
}

type Props = {
  id: string;
  value: string;
  onChange: (next: string) => void;
  slots: PromptSlot[];
  maxLength?: number;
};

export default function StudioPromptField({ id, value, onChange, slots, maxLength = 8000 }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const lastEmitted = useRef(value);
  const composing = useRef(false);
  const [caret, setCaret] = useState(0);
  const [menuOn, setMenuOn] = useState(false);
  const [hi, setHi] = useState(0);
  const [menuPos, setMenuPos] = useState({ left: 12, top: 40 });

  const named = useMemo(() => slots.filter((s) => displayName(s)), [slots]);
  const query = slashQueryAt(value, caret, named);
  const filtered = useMemo(() => {
    if (!query) return named;
    const q = query.query.toLowerCase();
    return named.filter((s) => {
      const name = displayName(s).toLowerCase();
      return name.startsWith(q) || s.key.toLowerCase().startsWith(q) || name.includes(q);
    });
  }, [named, query]);

  const exact = Boolean(query && named.some((s) => displayName(s).toLowerCase() === query.query.toLowerCase()));
  const open = menuOn && Boolean(query) && !exact;

  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value === lastEmitted.current && el.dataset.hydrated === '1') return;
    lastEmitted.current = value;
    const keep = document.activeElement === el ? caretOffset(el) : null;
    fillEditor(el, value, named);
    el.dataset.hydrated = '1';
    if (keep != null) setCaretOffset(el, keep);
  }, [value, named]);

  useEffect(() => {
    setHi(0);
  }, [query?.query, open]);

  useEffect(() => {
    const onSel = () => {
      const el = editorRef.current;
      if (!el || document.activeElement !== el) return;
      setCaret(caretOffset(el));
    };
    document.addEventListener('selectionchange', onSel);
    return () => document.removeEventListener('selectionchange', onSel);
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rect = caretClientRect();
    const box = wrap.getBoundingClientRect();
    let left = 14;
    let top = 36;
    if (rect) {
      left = rect.left - box.left;
      top = rect.bottom - box.top + 8;
    }
    const menuW = 240;
    setMenuPos({
      left: Math.max(8, Math.min(left, Math.max(8, box.width - menuW - 8))),
      top: Math.max(8, top),
    });
  }, [open, caret, value]);

  const emit = (next: string, pos?: number) => {
    const el = editorRef.current;
    const clipped = next.slice(0, maxLength);
    lastEmitted.current = clipped;
    if (el) {
      fillEditor(el, clipped, named);
      const at = pos == null ? clipped.length : pos;
      el.focus();
      setCaretOffset(el, at);
      setCaret(at);
    }
    onChange(clipped);
  };

  const syncFromDom = () => {
    const el = editorRef.current;
    if (!el || composing.current) return;
    const next = serializeEditor(el).slice(0, maxLength);
    const segs = tokenizePrompt(next, named);
    const chips = el.querySelectorAll('[data-ref]').length;
    const refs = segs.filter((s) => s.t === 'ref').length;
    lastEmitted.current = next;
    if (chips !== refs) {
      const at = caretOffset(el);
      fillEditor(el, next, named);
      setCaretOffset(el, at);
      setCaret(at);
    } else {
      setCaret(caretOffset(el));
    }
    el.classList.toggle('is-empty', !next);
    setMenuOn(Boolean(slashQueryAt(next, caretOffset(el), named)));
    onChange(next);
  };

  const pick = (slot: PromptSlot) => {
    const name = displayName(slot);
    if (!name) return;
    const start = query ? query.start : caret;
    emit(`${value.slice(0, start)}/${name}${value.slice(caret)}`, start + name.length + 1);
    setMenuOn(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (open && filtered.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHi((n) => (n + 1) % filtered.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHi((n) => (n - 1 + filtered.length) % filtered.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        if (filtered[hi]) {
          e.preventDefault();
          pick(filtered[hi]);
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMenuOn(false);
        return;
      }
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertText', false, '\n');
    }
  };

  return (
    <div className="studio-prompt" ref={wrapRef}>
      <div
        id={id}
        ref={editorRef}
        className={`studio-prompt-editor${value ? '' : ' is-empty'}`}
        role="textbox"
        aria-multiline="true"
        aria-label="Prompt"
        contentEditable
        suppressContentEditableWarning
        spellCheck={false}
        data-placeholder="Descreva a cena. Digite / para inserir um parâmetro…"
        onCompositionStart={() => { composing.current = true; }}
        onCompositionEnd={() => { composing.current = false; syncFromDom(); }}
        onInput={() => {
          syncFromDom();
        }}
        onKeyDown={onKeyDown}
        onPaste={(e) => {
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
        }}
        onFocus={() => {
          const el = editorRef.current;
          if (el && !serializeEditor(el)) {
            el.replaceChildren();
            el.classList.add('is-empty');
          }
        }}
      />
      {open && (
        <ul
          id="studio-prompt-list"
          className="studio-prompt-menu"
          role="listbox"
          aria-label="Parâmetros"
          style={{ left: menuPos.left, top: menuPos.top }}
        >
          {named.length === 0 ? (
            <li className="is-empty">Cadastre um parâmetro para usar com /.</li>
          ) : filtered.length === 0 ? (
            <li className="is-empty">Nenhum parâmetro com esse nome.</li>
          ) : filtered.map((slot, i) => (
            <li key={`${slot.key}-${i}`}>
              <button
                type="button"
                role="option"
                aria-selected={i === hi}
                className={i === hi ? 'is-on' : ''}
                onMouseDown={(e) => { e.preventDefault(); pick(slot); }}
              >
                <span>/{displayName(slot)}</span>
                {slot.slot_type && slot.slot_type !== 'text' ? <em>{slot.slot_type === 'image_start' ? 'imagem inicial' : slot.slot_type === 'image_end' ? 'imagem final' : 'referência'}</em> : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function rewriteSlotRefs(
  prompt: string,
  from: { label: string; key: string },
  to: { label: string; key: string },
): string {
  let next = prompt;
  if (from.key && from.key !== to.key) next = next.split(`{{${from.key}}}`).join(to.key ? `{{${to.key}}}` : '');
  const oldName = (from.label || from.key).trim();
  const newName = (to.label || to.key).trim();
  if (oldName && oldName !== newName) {
    const re = new RegExp(`/${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\wÀ-ÿ])`, 'gi');
    next = next.replace(re, newName ? `/${newName}` : '');
  }
  return next;
}
